import React, { useState, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { Stack, CaretDown, CaretRight, Spinner } from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import CumulativeTimeline from '../operationalDashboard/components/CumulativeTimeline';

interface CumulativeLabel {
  labelId: number;
  labelName: string;
  questionId: number;
}

interface CumulativeDocumentData {
  documents: Array<{
    id: number;
    fileName?: string;
    label?: string[];
    sharePointFileUrl?: string;
    documentDate?: string;
    expirationDate?: string;
    summary?: string;
    status?: string;
    createdAt: string;
  }>;
  synthesis?: string;
  labelInfo: {
    id: number;
    label: string;
    documentBehavior: string;
  };
}

interface DueDiligenceCumulativeSectionProps {
  cumulativeLabels: CumulativeLabel[];
  buildingId?: string;
  unitId?: string;
}

/**
 * Section to display cumulative document timelines for labels.
 * Shows all labels with documentBehavior === 'cumulative'.
 */
const DueDiligenceCumulativeSection: React.FC<DueDiligenceCumulativeSectionProps> = ({
  cumulativeLabels,
  buildingId,
  unitId,
}) => {
  const { session } = useSession();
  const [expandedTimeline, setExpandedTimeline] = useState<number | null>(null);
  const [cumulativeData, setCumulativeData] = useState<Record<number, CumulativeDocumentData>>({});
  const [loadingCumulative, setLoadingCumulative] = useState<Set<number>>(new Set());

  // Fetch cumulative documents for a label
  const fetchCumulativeDocuments = useCallback(
    async (labelId: number) => {
      if (!session || !buildingId) return;

      // Don't refetch if we already have data
      if (cumulativeData[labelId]) return;

      setLoadingCumulative((prev) => new Set(prev).add(labelId));
      try {
        const params = new URLSearchParams({ buildingId });
        if (unitId) params.append('unitId', unitId);

        const response = await fetchWithAuth(
          `${API_URL}/due-diligence/aggregation/documents-by-label/${labelId}?${params}`,
          { method: 'GET' },
          session
        );

        if (!response.ok) {
          throw new Error('Failed to fetch cumulative documents');
        }

        const data = await response.json();
        setCumulativeData((prev) => ({ ...prev, [labelId]: data }));
      } catch (error) {
        console.error('Error fetching cumulative documents:', error);
        toast.error('Kon documentengeschiedenis niet laden');
      } finally {
        setLoadingCumulative((prev) => {
          const next = new Set(prev);
          next.delete(labelId);
          return next;
        });
      }
    },
    [session, buildingId, unitId, cumulativeData]
  );

  // Toggle cumulative timeline
  const toggleTimeline = useCallback(
    (labelId: number) => {
      if (expandedTimeline === labelId) {
        setExpandedTimeline(null);
      } else {
        setExpandedTimeline(labelId);
        fetchCumulativeDocuments(labelId);
      }
    },
    [expandedTimeline, fetchCumulativeDocuments]
  );

  if (cumulativeLabels.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-violet-700 flex items-center gap-2">
        <Stack weight="duotone" size={18} />
        Cumulatieve Documenten
      </h2>

      {cumulativeLabels.map(({ labelId, labelName }) => {
        const isExpanded = expandedTimeline === labelId;
        const isLoadingTimeline = loadingCumulative.has(labelId);
        const data = cumulativeData[labelId];
        const docCount = data?.documents?.length ?? 0;

        return (
          <div
            key={labelId}
            className="border border-violet-200 rounded-lg overflow-hidden bg-gradient-to-r from-violet-50/50 to-blue-50/50"
          >
            <button
              onClick={() => toggleTimeline(labelId)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-violet-50/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Stack weight="duotone" size={18} className="text-violet-600" />
                <span className="font-medium text-sm text-violet-800">
                  {labelName}
                </span>
                <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">
                  Cumulatief
                </span>
                {docCount > 0 && (
                  <span className="text-xs text-neutral-secondary-mid">
                    ({docCount} {docCount === 1 ? 'document' : 'documenten'})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isLoadingTimeline && (
                  <Spinner size={14} className="animate-spin text-violet-500" />
                )}
                {isExpanded ? (
                  <CaretDown size={14} className="text-violet-500" />
                ) : (
                  <CaretRight size={14} className="text-violet-500" />
                )}
              </div>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 border-t border-violet-100">
                <CumulativeTimeline
                  documents={data?.documents ?? []}
                  isLoading={isLoadingTimeline}
                  synthesis={data?.synthesis}
                  labelName={labelName}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DueDiligenceCumulativeSection;
